import { Section } from "@/components/luminous/interior";
import type { Lang } from "@/components/luminous/primitives";

interface CaseStudiesHubFAQProps {
  language: Lang;
}

const content = {
  es: {
    label: "Preguntas",
    title: "Preguntas sobre los casos",
    faqs: [
      { question: "¿Los resultados varían de un caso a otro?", answer: "Sí, absolutamente. Cada proyecto es único y los resultados dependen de múltiples factores: industria, nivel de competencia, estado inicial del proyecto, recursos disponibles y tiempo de implementación. Por eso mostramos el proceso completo, no solo los números finales." },
      { question: "¿Puedo ver métricas específicas de los casos?", answer: "Los casos publicados muestran resultados observables sin revelar datos confidenciales de los clientes. En la reunión inicial gratuita puedo compartir más detalles sobre casos similares a tu industria si es relevante para tu decisión." },
      { question: "¿Trabajas con empresas similares a la mía?", answer: "Mi especialización es en empresas B2B y marcas de consumo: fabricantes, distribuidores y proveedores de servicios profesionales. La reunión inicial gratuita es la mejor forma de confirmarlo." },
      { question: "¿Por qué no muestras porcentajes exactos de crecimiento?", answer: "Porque los porcentajes sin contexto pueden ser engañosos. Prefiero mostrar el proceso, los problemas resueltos y los cambios observables, con periodo y fuente cuando existen. Eso da mejor información para decidir." },
      { question: "¿Puedo hablar con alguno de tus clientes?", answer: "Algunos clientes están dispuestos a compartir su experiencia bajo acuerdo de confidencialidad. Si después del diagnóstico inicial decides avanzar, puedo facilitar esa conversación según el caso." },
    ],
  },
  en: {
    label: "Questions",
    title: "Questions about the cases",
    faqs: [
      { question: "Do results vary from case to case?", answer: "Yes, absolutely. Each project is unique and results depend on multiple factors: industry, competition level, initial project state, available resources and implementation time. That's why we show the complete process, not just the final numbers." },
      { question: "Can I see specific metrics from the cases?", answer: "Published cases show observable results without revealing confidential client data. In the free initial consultation I can share more details about cases similar to your industry if relevant to your decision." },
      { question: "Do you work with companies similar to mine?", answer: "My specialization is in B2B companies and consumer brands: manufacturers, distributors and professional service providers. The free initial consultation is the best way to confirm." },
      { question: "Why don't you show exact growth percentages?", answer: "Because percentages without context can be misleading. I prefer to show the process, problems solved and observable changes, with period and source when they exist. That gives you better information to decide." },
      { question: "Can I talk to any of your clients?", answer: "Some clients are willing to share their experience under confidentiality agreement. If after the initial diagnosis you decide to move forward, I can facilitate that conversation depending on the case." },
    ],
  },
} as const;

export const CaseStudiesHubFAQ = ({ language }: CaseStudiesHubFAQProps) => {
  const t = content[language];

  return (
    <Section id="preguntas" index="03" label={t.label} title={t.title} tone="white">
      <div>
        {t.faqs.map((faq) => (
          <details key={faq.question} className="v3-details">
            <summary>{faq.question}</summary>
            <div className="v3-details-body">{faq.answer}</div>
          </details>
        ))}
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: t.faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: { "@type": "Answer", text: faq.answer },
            })),
          }),
        }}
      />
    </Section>
  );
};
